import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUserId,
  getDealApplicationId,
  getIsInterimDashboard,
  getNhDealApplicationId,
  getSessionLoanKey,
  getSessionLosType,
  getSessionVestaBorrowerId,
  getTaskCount,
  setTaskCount,
  TaskFilterType,
} from "features/authSlice";
import {
  NeedItem,
  useCreateDashboardSessionMutation,
  useCreateNeedsMutation,
  useGetDashboardItemsQuery,
  useGetNeedsQuery,
  useGetVestaConditionsQuery,
} from "pages/dashboard/home/dashboardService";
import { getSessionHeldUnderReviewIds } from "pages/dashboard/store/dashboardSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  DASHBOARD_CREATE_NEEDS_TRIGGERED,
  DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE,
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { getEffectiveTaskStatusForCount } from "utilities/needsHelper";
import {
  isVestaLoanSession,
  mapVestaConditionsToNeedItems,
  shouldFetchVestaConditions,
} from "utilities/vestaConditions";

const getDashboardSessionStorageKey = (userId: string, nhDealApplicationId: string) =>
  `dashboardSession_${userId}_${nhDealApplicationId}`;

const buildDashboardSession = (): DashboardSession => {
  let dashboardSessionId = sessionStorage.getItem(DASHBOARD_SESSION_ID);
  let dashboardSessionStartTimestamp = sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP);

  if (!dashboardSessionId) {
    dashboardSessionId = crypto.randomUUID();
    dashboardSessionStartTimestamp = new Date().toISOString();
    sessionStorage.setItem(DASHBOARD_SESSION_ID, dashboardSessionId);
    sessionStorage.setItem(DASHBOARD_SESSION_START_TIMESTAMP, dashboardSessionStartTimestamp);
  }

  return {
    dashboardSessionId,
    dashboardSessionStartTimestamp: dashboardSessionStartTimestamp ?? new Date().toISOString(),
    dashboardSessionEndTimestamp: null,
  };
};

interface DashboardSession {
  dashboardSessionId: string;
  dashboardSessionStartTimestamp: string | null;
  dashboardSessionEndTimestamp: string | null;
}

interface DashboardNeedsOrchestrationResult {
  needsList: NeedItem[];
  isNeedsListLoading: boolean;
  isNeedsListFetching: boolean;
  needsListError: boolean;
  refetchNeedsList: () => Promise<{ data?: NeedItem[] } | undefined>;
  isCreatingNeeds: boolean;
  isDashboardItemsLoading: boolean;
}

export function useDashboardNeedsOrchestration(): DashboardNeedsOrchestrationResult {
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const isInterimDashboard = useSelector(getIsInterimDashboard);
  const sidebarTaskCount = useSelector(getTaskCount);
  const sessionHeldUnderReviewIds = useSelector(getSessionHeldUnderReviewIds);
  const losType = useSelector(getSessionLosType);
  const loanKey = useSelector(getSessionLoanKey);
  const vestaBorrowerId = useSelector(getSessionVestaBorrowerId);
  const { data: featureFlags } = useGetFeatureFlagsQuery();

  const isVestaLoan = isVestaLoanSession(losType);
  const fetchVestaConditions = shouldFetchVestaConditions({
    losType,
    vestaConditionsEnabled: featureFlags?.vestaConditionsEnabled,
    loanKey,
    vestaBorrowerId,
  });
  // Vesta loan: never call Encompass Needs / createNeeds / Enhanced Conditions (ADO-548030).
  const useEncompassNeedsPath = !isVestaLoan;

  const [needsList, setNeedsList] = useState<NeedItem[]>([]);
  const [isTaskCountSet, setIsTaskCountSet] = useState(false);
  const [shouldFetchNeedsList, setShouldFetchNeedsList] = useState(false);
  const [isDashboardSessionReady, setIsDashboardSessionReady] = useState(false);
  const [createDashboardSession] = useCreateDashboardSessionMutation();

  const { data: dashboardData, isLoading: isDashboardItemsLoading } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId },
    { skip: !nhDealApplicationId || !userId },
  );
  const [createNeeds, { isLoading: isCreatingNeeds, isSuccess: isCreateNeedsSucceeded }] = useCreateNeedsMutation();

  useEffect(() => {
    if (!userId || !nhDealApplicationId) {
      return;
    }

    const sessionKey = getDashboardSessionStorageKey(userId, nhDealApplicationId);
    if (sessionStorage.getItem(sessionKey) === "true") {
      setIsDashboardSessionReady(true);
      return;
    }

    void createDashboardSession({
      nhDealApplicationId,
      userId,
      dashboardSession: buildDashboardSession(),
    })
      .unwrap()
      .then(() => {
        sessionStorage.setItem(sessionKey, "true");
        setIsDashboardSessionReady(true);
      })
      .catch((error: unknown) => {
        console.error("Failed to create dashboard session:", error);
        setIsDashboardSessionReady(true);
      });
  }, [userId, nhDealApplicationId, createDashboardSession]);

  useEffect(() => {
    if (!useEncompassNeedsPath) {
      setShouldFetchNeedsList(false);
      return;
    }

    if (!isDashboardSessionReady || !dashboardData || isCreatingNeeds) return;

    const hasCreateNeedsTriggeredForSession =
      sessionStorage.getItem(DASHBOARD_CREATE_NEEDS_TRIGGERED) === DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE;
    const shouldEvaluateNeedsCreation =
      (dashboardData?.isAwaitingNeedsCreation === true ||
        (isInterimDashboard && dashboardData?.isNew && dashboardData?.isCoborrowerNew)) &&
      !hasCreateNeedsTriggeredForSession;

    if (shouldEvaluateNeedsCreation) {
      if (!dashboardData?.isApplicationFormRequired) {
        setShouldFetchNeedsList(true);
      } else if (!isCreateNeedsSucceeded && !shouldFetchNeedsList) {
        void createNeeds({ dealApplicationId, userId, isInterimDashboard });
        sessionStorage.setItem(DASHBOARD_CREATE_NEEDS_TRIGGERED, DASHBOARD_CREATE_NEEDS_TRIGGERED_TRUE);
      }
    } else {
      setShouldFetchNeedsList(true);
    }
  }, [
    useEncompassNeedsPath,
    dashboardData?.isNew,
    dashboardData?.isCoborrowerNew,
    dashboardData?.isAwaitingNeedsCreation,
    dashboardData?.isApplicationFormRequired,
    isCreatingNeeds,
    isInterimDashboard,
    isCreateNeedsSucceeded,
    shouldFetchNeedsList,
    createNeeds,
    dealApplicationId,
    userId,
    isDashboardSessionReady,
  ]);

  useEffect(() => {
    if (!useEncompassNeedsPath) {
      return;
    }

    if (isCreateNeedsSucceeded && !shouldFetchNeedsList) {
      setShouldFetchNeedsList(true);
    }
  }, [useEncompassNeedsPath, isCreateNeedsSucceeded, shouldFetchNeedsList]);

  const needsQueryResult = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: !useEncompassNeedsPath || isCreatingNeeds || !shouldFetchNeedsList || !isDashboardSessionReady,
      refetchOnMountOrArgChange: false,
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data,
        isLoading,
        isFetching,
        error,
      }),
    },
  );

  const vestaQueryResult = useGetVestaConditionsQuery(
    { loanId: loanKey ?? "", vestaBorrowerId: vestaBorrowerId ?? "" },
    {
      skip: !fetchVestaConditions,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data,
        isLoading,
        isFetching,
        error,
      }),
    },
  );

  const encompassNeedItems = needsQueryResult.data;
  const vestaNeedItems = useMemo(
    () => mapVestaConditionsToNeedItems(vestaQueryResult.data),
    [vestaQueryResult.data],
  );

  let isNeedsListLoading = false;
  if (useEncompassNeedsPath) {
    isNeedsListLoading = needsQueryResult.isLoading;
  } else if (fetchVestaConditions) {
    isNeedsListLoading = vestaQueryResult.isLoading;
  }

  let isNeedsListFetching = false;
  if (useEncompassNeedsPath) {
    isNeedsListFetching = needsQueryResult.isFetching;
  } else if (fetchVestaConditions) {
    isNeedsListFetching = vestaQueryResult.isFetching;
  }

  let needsListError = false;
  if (useEncompassNeedsPath) {
    needsListError = Boolean(needsQueryResult.error);
  } else if (fetchVestaConditions) {
    needsListError = Boolean(vestaQueryResult.error);
  }

  const refetchNeedsList = async (): Promise<{ data?: NeedItem[] } | undefined> => {
    if (useEncompassNeedsPath) {
      const result = await needsQueryResult.refetch();
      return { data: result.data };
    }

    if (fetchVestaConditions) {
      const result = await vestaQueryResult.refetch();
      return { data: mapVestaConditionsToNeedItems(result.data) };
    }

    return { data: [] };
  };

  useEffect(() => {
    if (useEncompassNeedsPath) {
      if (!isNeedsListLoading && !needsListError && encompassNeedItems) {
        setNeedsList(encompassNeedItems);

        const matchingNeeds = encompassNeedItems.filter(
          (item) => getEffectiveTaskStatusForCount(item, sessionHeldUnderReviewIds) === TaskFilterType.ToDo,
        ).length;
        if (!isTaskCountSet || sidebarTaskCount !== matchingNeeds) {
          dispatch(setTaskCount(matchingNeeds));
          setIsTaskCountSet(true);
        }
      }
      return;
    }

    // Vesta path: empty when flag off or ids missing; map conditions when fetch succeeds.
    // Keep prior empty array identity — setNeedsList([]) every run would infinite-loop (ADO-548030).
    if (!fetchVestaConditions) {
      setNeedsList((prev) => (prev.length === 0 ? prev : []));
      if (!isTaskCountSet || sidebarTaskCount !== 0) {
        dispatch(setTaskCount(0));
        setIsTaskCountSet(true);
      }
      return;
    }

    if (!isNeedsListLoading && !needsListError) {
      setNeedsList((prev) => (prev === vestaNeedItems ? prev : vestaNeedItems));

      const matchingNeeds = vestaNeedItems.filter(
        (item) => getEffectiveTaskStatusForCount(item, sessionHeldUnderReviewIds) === TaskFilterType.ToDo,
      ).length;
      if (!isTaskCountSet || sidebarTaskCount !== matchingNeeds) {
        dispatch(setTaskCount(matchingNeeds));
        setIsTaskCountSet(true);
      }
    }
  }, [
    useEncompassNeedsPath,
    fetchVestaConditions,
    encompassNeedItems,
    vestaNeedItems,
    isNeedsListLoading,
    needsListError,
    sessionHeldUnderReviewIds,
    isTaskCountSet,
    sidebarTaskCount,
    dispatch,
  ]);

  return {
    needsList,
    isNeedsListLoading,
    isNeedsListFetching,
    needsListError,
    refetchNeedsList,
    isCreatingNeeds: useEncompassNeedsPath ? isCreatingNeeds : false,
    isDashboardItemsLoading,
  };
}
